import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { 
  BookOpen, Clock, Award, Code2, CheckCircle, 
  GraduationCap, Target, Star, Info
} from 'lucide-react';
import { coursesApi } from '../lib/api';

export default function CourseDescriptionModal({ courseId, isOpen, onClose }) {
  const [description, setDescription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && courseId) {
      fetchDescription();
    }
  }, [isOpen, courseId]);

  const fetchDescription = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await coursesApi.getDescription(courseId);
      setDescription(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-500/10 text-green-500';
      case 'intermediate': return 'bg-amber-500/10 text-amber-500';
      case 'advanced': return 'bg-red-500/10 text-red-500';
      default: return 'bg-gray-500/10 text-gray-500';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'programming': return <Code2 className="w-5 h-5" />;
      case 'life_skills': return <Star className="w-5 h-5" />;
      case 'financial': return <Award className="w-5 h-5" />;
      case 'career': return <GraduationCap className="w-5 h-5" />;
      default: return <BookOpen className="w-5 h-5" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Info className="w-5 h-5 text-primary" />
            Course Details
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] px-6 pb-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-500">
              <p>{error}</p>
              <Button variant="outline" className="mt-4" onClick={fetchDescription}>
                Try Again
              </Button>
            </div>
          ) : description ? (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${getDifficultyColor(description.difficulty)}`}>
                  {getCategoryIcon(description.category)}
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{description.title}</h2>
                  <div className="flex items-center gap-3 mt-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(description.difficulty)}`}>
                      {description.difficulty?.toUpperCase()}
                    </span>
                    <span className="text-sm text-muted-foreground capitalize">
                      {description.category?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-muted/50 rounded-xl p-4 text-center">
                  <BookOpen className="w-6 h-6 mx-auto text-primary mb-2" />
                  <div className="text-2xl font-bold">{description.chapter_count}</div>
                  <div className="text-xs text-muted-foreground">Chapters</div>
                </div>
                <div className="bg-muted/50 rounded-xl p-4 text-center">
                  <Clock className="w-6 h-6 mx-auto text-primary mb-2" />
                  <div className="text-2xl font-bold">{description.duration_hours || '~20'}</div>
                  <div className="text-xs text-muted-foreground">Hours</div>
                </div>
                <div className="bg-muted/50 rounded-xl p-4 text-center">
                  <Target className="w-6 h-6 mx-auto text-primary mb-2" />
                  <div className="text-2xl font-bold">{description.exam_info?.total_questions || 30}</div>
                  <div className="text-xs text-muted-foreground">Exam Questions</div>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  About This Course
                </h3>
                <p className="text-muted-foreground whitespace-pre-line">
                  {description.detailed_description}
                </p>
              </div>

              {/* What You'll Learn */}
              {description.what_you_learn && description.what_you_learn.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" />
                    What You'll Learn
                  </h3>
                  <div className="grid gap-2">
                    {description.what_you_learn.map((item, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Prerequisites */}
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Prerequisites
                </h3>
                <p className="text-sm text-muted-foreground">
                  {description.prerequisites}
                </p>
              </div>

              {/* Exam Info */}
              {description.exam_info && (
                <div className="bg-primary/5 rounded-xl p-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Award className="w-4 h-4 text-primary" />
                    Final Exam
                  </h3>
                  <div className="grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <div className="font-bold text-lg">{description.exam_info.total_questions}</div>
                      <div className="text-muted-foreground">Questions</div>
                    </div>
                    <div>
                      <div className="font-bold text-lg">{description.exam_info.time_limit_minutes} min</div>
                      <div className="text-muted-foreground">Time Limit</div>
                    </div>
                    <div>
                      <div className="font-bold text-lg">{description.exam_info.passing_score}%</div>
                      <div className="text-muted-foreground">Pass Score</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Features */}
              <div className="flex flex-wrap gap-2">
                {description.has_code_lab && (
                  <span className="px-3 py-1 rounded-full bg-blue-500/10 text-blue-500 text-xs font-medium">
                    Interactive Code Lab
                  </span>
                )}
                {description.has_exam && (
                  <span className="px-3 py-1 rounded-full bg-amber-500/10 text-amber-500 text-xs font-medium">
                    Final Exam
                  </span>
                )}
                <span className="px-3 py-1 rounded-full bg-green-500/10 text-green-500 text-xs font-medium">
                  Certificate on Completion
                </span>
                {!description.has_code_lab && (
                  <span className="px-3 py-1 rounded-full bg-purple-500/10 text-purple-500 text-xs font-medium">
                    Multi-Language Support
                  </span>
                )}
              </div>
            </div>
          ) : null}
        </ScrollArea>

        <div className="p-6 pt-0">
          <Button className="w-full" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
